import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit, faTrash, faEye, faSave, faSearch, faRefresh,
    faBoxes, faSpinner, faExclamationTriangle, faCheckCircle,
    faTimes, faChevronLeft, faChevronRight, faFilter,
    faCalendarAlt, faFilePdf, faPrint, faDownload,
    faTimesCircle, faBox, faTag, faBuilding, faBalanceScale,
    faDollarSign, faCalculator, faExclamationCircle, faHashtag,
    faPlus, faBarcode, faLayerGroup, faIndustry, faWeightHanging,
    faWarehouse, faFileExport, faChartLine, faPercent, faUser,
    faPalette, faRulerCombined, faBoxes as faBoxesIcon, faInfoCircle,
    faHistory, faStore, faExchangeAlt, faShoppingCart, faTruck,
    faShip, faRoad, faMoneyBillWave, faUniversity, faMoneyCheck,
    faCreditCard, faPhone, faEnvelope, faMapMarkerAlt, faGlobe,
    faClock, faCalendarCheck, faFileInvoice, faReceipt, faMoneyBill,
    faDownload as faDownloadIcon, faMinus, faArrowsAlt, faExpand,
    faCompress, faCubes, faTrashAlt, faPlusCircle, faEdit as faEditIcon,
    faListOl, faCubes as faCubesIcon
} from '@fortawesome/free-solid-svg-icons';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createPlaceOrderListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_placeorder_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => cache.set(key, value));
            }
        } catch (e) { console.log("No cache found in Place Order List"); }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => { cacheObj[key] = value; });
            localStorage.setItem('ourzest_placeorder_list_translations', JSON.stringify(cacheObj));
        } catch (e) { console.error("Error saving to cache:", e); }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) { const data = await response.json(); return data.translatedText; }
        } catch (e) { console.log("LibreTranslate failed"); }
        try {
            const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`);
            if (response.ok) { const data = await response.json(); return data.responseData.translatedText; }
        } catch (e) { console.log("MyMemory translation failed"); }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        placeOrderList: 'Purchase Orders List',
        allPlaceOrders: 'My Purchase Orders',
        purchaseOrders: 'Purchase Orders',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search purchase orders...',
        refresh: 'Refresh',
        loadingPlaceOrders: 'Loading purchase orders...',
        noPlaceOrdersFound: 'No purchase orders found',
        orderDetails: 'Purchase Order Details',
        close: 'Close',
        serialNo: 'S.No',
        id: 'ID',
        orderNumber: 'Order #',
        orderDate: 'Order Date',
        expectedDelivery: 'Expected Delivery',
        supplier: 'Supplier',
        supplierName: 'Supplier Name',
        company: 'Company',
        totalAmount: 'Total Amount',
        paidAmount: 'Paid',
        balance: 'Balance',
        status: 'Status',
        paymentMethod: 'Payment',
        manualTotalYards: 'Total Yards',
        manualTotalRolls: 'Total Rolls',
        containerNo: 'Container',
        routeDetails: 'Route',
        itemsCount: 'Items',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',
        currency: 'Currency',
        exchangeRate: 'Exchange Rate',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Are you sure you want to delete this purchase order?',
        deletedSuccess: 'Purchase order deleted successfully',
        deleteError: 'Error deleting purchase order',
        networkError: 'Network error occurred',
        orderId: 'Order ID',
        supplierInfo: 'Supplier Information',
        paymentInfo: 'Payment Information',
        shippingInfo: 'Shipping Information',
        productDetails: 'Product Details',
        productName: 'Product',
        colorCode: 'Color',
        category: 'Category',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        totalYards: 'Total Yards',
        totalRolls: 'Total Rolls',
        manualTotals: 'Manual Totals',
        notProvided: 'Not provided',
        inStock: 'In Stock',
        lowStock: 'Low Stock',
        outOfStock: 'Out of Stock',
        orderPlaced: 'Order Placed',
        processing: 'Processing',
        shipped: 'Shipped',
        delivered: 'Delivered',
        cancelled: 'Cancelled',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        cheque: 'Cheque',
        creditCard: 'Credit Card',
        other: 'Other',
        showing: 'Showing',
        of: 'of',
        orders: 'orders',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        orderNumberFilter: 'Order Number',
        supplierFilter: 'Supplier',
        statusFilter: 'Status',
        paymentMethodFilter: 'Payment Method',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        addedByFilter: 'Added By',
        editOrder: 'Edit Purchase Order',
        updateOrder: 'Update Order',
        updating: 'Updating...',
        orderUpdated: 'Purchase order updated successfully',
        updateError: 'Error updating purchase order',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the purchase order details below',
        supplierRequired: 'Supplier is required',
        orderDateRequired: 'Order date is required',
        statusRequired: 'Status is required',
        itemsRequired: 'At least one item is required',
        invalidAmount: 'Please enter a valid amount',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this purchase order?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        myRecords: 'My Records Only',
        loadingUserData: 'Loading your purchase order data...',
        noUserRecords: 'No purchase orders found in this shop',
        businessReport: 'Business Report',
        generatedBy: 'Generated by',
        purchaseOrderReport: 'Purchase Order Report',
        orderSummary: 'Order Summary',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalOrders: 'Total Orders',
        pendingAmount: 'Pending Amount',
        totalValue: 'Total Value',
        dateAdded: 'Date Added',
        lastUpdated: 'Last Updated',
        remarks: 'Remarks',
        bankDetails: 'Bank Details',
        chequeDetails: 'Cheque Details',
        cardDetails: 'Card Details',
        baseCurrency: 'Base Currency',
        convertedAmount: 'Converted Amount',
        originalCurrency: 'Original Currency',
        yards: 'Yards',
        meters: 'Meters',
        generatingPDF: 'Generating PDF...',
        pdfGenerationError: 'Error generating PDF',
        pdfGenerated: 'PDF generated successfully',
        reportFilters: 'Applied Filters',
        reportDate: 'Report Date',
        totalYardsRequired: 'Total yards is required',
        priceOptional: 'Price',
        optional: 'Optional',
        addItem: 'Add Item',
        removeItem: 'Remove Item',
        searchProduct: 'Search product...',
        noProductsFound: 'No products found',
        productNameColor: 'Product & Color',
        totalAmountLabel: 'Total Amount',
        currencySymbol: 'Rs',
        orderQuantity: 'Order Quantity',
        orderRolls: 'Order Rolls',
        unit: 'Unit',
        selectUnit: 'Unit',
        fromBankName: 'From Bank Name',
        fromAccountNumber: 'From Account Number',
        toBankName: 'To Bank Name',
        toAccountNumber: 'To Account Number',
        bankReference: 'Reference / UTR',
        paymentReceipt: 'Payment Receipt',
        chequeNumber: 'Cheque Number',
        chequeDate: 'Cheque Date',
        chequeBankName: 'Cheque Bank',
        chequeBranch: 'Cheque Branch',
        chequeImage: 'Cheque Image',
        cardLastFour: 'Last 4 Digits',
        cardHolderName: 'Card Holder Name',
        cardExpiry: 'Expiry Date',
        cardTransactionId: 'Transaction ID',
        billReceipt: 'Bill / Invoice Receipt',
        transfer: 'Transfer (Transaction OUT)'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(translateText(defaultTexts[key], lang).then(translated => { translations[key] = translated; }));
            }
        });
        if (promises.length > 0) { await Promise.all(promises); saveToCache(); }
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const placeOrderListTranslator = createPlaceOrderListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;
    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`} style={{ opacity }}>
            <div className="toast-content">
                <div className="toast-icon"><FontAwesomeIcon icon={getIconForType(toast.type)} /></div>
                <div className="toast-text">
                    <div className="toast-title">{getTitleForType(toast.type)}</div>
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div className="toast-progress-bar" style={{ width: `${toast.progress}%`, transition: 'width 0.1s linear' }}></div>
                <div className="toast-countdown">{toast.timeLeft}s</div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops"><p>{translations.noShops || 'No shops available'}</p></div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div key={shop.shop_id} className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`} onClick={() => handleShopSelect(shop)}>
                                    <div className="shop-item-icon"><FontAwesomeIcon icon={faStore} /></div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (<div className="shop-item-current"><FontAwesomeIcon icon={faCheckCircle} /><span>{translations.current || 'Current'}</span></div>)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Edit Items Table Component for Edit Modal - UPDATED with new fields
const EditOrderItemsTable = ({ items, onItemChange, onRemove, onAdd, errors, disabled, translations, isRTL, currencySymbol, products, filteredProducts, setFilteredProducts, productSearchTerms, setProductSearchTerms, showProductDropdown, setShowProductDropdown, loadingProducts, productSearchRefs, handleProductSearch, selectProduct }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getProductDisplayName = (item) => {
        if (item.product_name && item.product_colorcode) return `${item.product_name} - ${item.product_colorcode}`;
        else if (item.product_name) return item.product_name;
        else if (item.product_colorcode) return item.product_colorcode;
        return '';
    };

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(0);
    };

    const unitOptions = [
        { value: 'yards', label: translations?.yards || 'Yards' },
        { value: 'meters', label: translations?.meters || 'Meters' }
    ];

    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4><FontAwesomeIcon icon={faBoxes} />{translations.productDetails || 'Products'}</h4>
                    <button type="button" className="btn-add-item-mobile" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>{translations.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => {
                        const totalAmount = calculateTotal(item.price, item.order_quantity);
                        return (
                            <div key={item.id || index} className="item-card-mobile">
                                <div className="item-card-header">
                                    <span className="item-number"><FontAwesomeIcon icon={faBox} />Item {index + 1}</span>
                                    <button type="button" className="item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                                <div className="item-card-content">
                                    <div className={`mobile-field ${errors[`product_name_${index}`] ? 'has-error' : ''}`} ref={el => { if (productSearchRefs) productSearchRefs.current[index] = el; }}>
                                        <label><FontAwesomeIcon icon={faSearch} />{translations.productName || 'Product Name'} & Color</label>
                                        <div className="product-search-container">
                                            <input type="text"
                                                value={productSearchTerms[index] !== undefined ? productSearchTerms[index] : getProductDisplayName(item)}
                                                onChange={(e) => handleProductSearch(index, e.target.value)}
                                                onFocus={() => { setShowProductDropdown(prev => ({ ...prev, [index]: true })); if (!productSearchTerms[index]) setFilteredProducts(products); }}
                                                placeholder={translations.searchProduct || 'Search product...'} disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'} autoComplete="off" />
                                            {loadingProducts && <div className="search-loading"><FontAwesomeIcon icon={faSpinner} spin /></div>}
                                            {showProductDropdown[index] && (
                                                <div className="product-dropdown">
                                                    {filteredProducts.length > 0 ? (
                                                        filteredProducts.map(product => (
                                                            <div key={product.id} className="product-option" onClick={() => selectProduct(index, product)}>
                                                                <div className="product-name">{product.product_name}{product.product_colorcode && ` - ${product.product_colorcode}`}</div>
                                                                <div className="product-stock">Stock: {product.total_yards || 0} yds</div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="product-dropdown empty"><FontAwesomeIcon icon={faExclamationTriangle} />{translations.noProductsFound || 'No products found'}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Unit Selector */}
                                    <div className="mobile-field">
                                        <label><FontAwesomeIcon icon={faRulerCombined} />{translations.selectUnit || 'Unit'}</label>
                                        <select 
                                            value={item.unit || 'yards'}
                                            onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {unitOptions.map(unit => (
                                                <option key={unit.value} value={unit.value}>{unit.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Order Quantity */}
                                    <div className={`mobile-field ${errors[`order_quantity_${index}`] ? 'has-error' : ''}`}>
                                        <label><FontAwesomeIcon icon={faListOl} />{translations.orderQuantity || 'Order Quantity'}</label>
                                        <input type="number" 
                                            value={item.order_quantity || ''}
                                            onChange={(e) => {
                                                onItemChange(index, 'order_quantity', e.target.value);
                                                if (!item.manual_total_yards) {
                                                    onItemChange(index, 'manual_total_yards', e.target.value);
                                                    onItemChange(index, 'quantity', e.target.value);
                                                }
                                            }}
                                            step="1" min="0" placeholder="Enter order quantity" disabled={disabled} dir="ltr" />
                                    </div>

                                    {/* Order Rolls */}
                                    <div className="mobile-field">
                                        <label><FontAwesomeIcon icon={faBoxes} />{translations.orderRolls || 'Order Rolls'}</label>
                                        <input type="number" 
                                            value={item.order_rolls || ''}
                                            onChange={(e) => {
                                                onItemChange(index, 'order_rolls', e.target.value);
                                                if (!item.manual_total_rolls && e.target.value) {
                                                    onItemChange(index, 'manual_total_rolls', e.target.value);
                                                }
                                            }}
                                            step="1" min="0" placeholder="Optional" disabled={disabled} dir="ltr" />
                                    </div>

                                    {/* Total Yards (Stock) - kept for reference */}
                                    <div className={`mobile-field ${errors[`manual_total_yards_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations.manualTotalYards || 'Total Yards (Stock)'}</label>
                                        <input type="number" value={item.manual_total_yards || ''}
                                            onChange={(e) => {
                                                onItemChange(index, 'manual_total_yards', e.target.value);
                                                onItemChange(index, 'quantity', e.target.value);
                                                if (!item.order_quantity) {
                                                    onItemChange(index, 'order_quantity', e.target.value);
                                                }
                                            }}
                                            step="1" min="0" placeholder="Enter total yards" disabled={disabled} dir="ltr" />
                                        {errors[`manual_total_yards_${index}`] && <div className="error-text"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`manual_total_yards_${index}`]}</div>}
                                    </div>

                                    {/* Price */}
                                    <div className={`mobile-field ${errors[`price_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations.priceOptional || 'Price'} <span className="optional-badge">{translations.optional || 'Optional'}</span></label>
                                        <input type="number" value={item.price || ''}
                                            onChange={(e) => onItemChange(index, 'price', e.target.value)}
                                            step="1" min="0" placeholder="0" disabled={disabled} dir="ltr" />
                                    </div>

                                    {/* Total Amount */}
                                    <div className="mobile-field">
                                        <label>{translations.totalAmount || 'Total Amount'}</label>
                                        <div className="total-display-mobile"><span>{currencySymbol} {totalAmount}</span></div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Desktop View
    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations.productDetails || 'Products'}</h4>
                <button type="button" className="btn-add-item-table" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="table-wrapper">
                <table className="items-table edit-items-table">
                    <thead>
                        <tr>
                            <th>{translations.productName || 'Product'} & Color</th>
                            <th>{translations.selectUnit || 'Unit'}</th>
                            <th>{translations.orderQuantity || 'Order Qty'}</th>
                            <th>{translations.orderRolls || 'Order Rolls'}</th>
                            <th>{translations.manualTotalYards || 'Total Yards (Stock)'}</th>
                            <th>{translations.priceOptional || 'Price'}</th>
                            <th>{translations.totalAmount || 'Total'}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const totalAmount = calculateTotal(item.price, item.order_quantity);
                            return (
                                <tr key={item.id || index}>
                                    <td>
                                        <div className="table-field product-search-field" ref={el => { if (productSearchRefs) productSearchRefs.current[index] = el; }}>
                                            <div className="product-search-container">
                                                <input type="text"
                                                    value={productSearchTerms[index] !== undefined ? productSearchTerms[index] : getProductDisplayName(item)}
                                                    onChange={(e) => handleProductSearch(index, e.target.value)}
                                                    onFocus={() => { setShowProductDropdown(prev => ({ ...prev, [index]: true })); if (!productSearchTerms[index]) setFilteredProducts(products); }}
                                                    placeholder={translations.searchProduct || 'Search product...'} disabled={disabled}
                                                    dir={isRTL ? 'rtl' : 'ltr'} autoComplete="off" />
                                                {loadingProducts && <div className="search-loading-small"><FontAwesomeIcon icon={faSpinner} spin /></div>}
                                                {showProductDropdown[index] && (
                                                    <div className="product-dropdown-table">
                                                        {filteredProducts.length > 0 ? (
                                                            filteredProducts.map(product => (
                                                                <div key={product.id} className="product-option" onClick={() => selectProduct(index, product)}>
                                                                    <div className="product-name">{product.product_name}{product.product_colorcode && ` - ${product.product_colorcode}`}</div>
                                                                    <div className="product-stock">Stock: {product.total_yards || 0} yds</div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="product-dropdown-table empty"><FontAwesomeIcon icon={faExclamationTriangle} />{translations.noProductsFound || 'No products found'}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <select 
                                                value={item.unit || 'yards'}
                                                onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            >
                                                {unitOptions.map(unit => (
                                                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="number" 
                                                value={item.order_quantity || ''}
                                                onChange={(e) => {
                                                    onItemChange(index, 'order_quantity', e.target.value);
                                                    if (!item.manual_total_yards) {
                                                        onItemChange(index, 'manual_total_yards', e.target.value);
                                                        onItemChange(index, 'quantity', e.target.value);
                                                    }
                                                }}
                                                step="1" min="0" placeholder="Qty" disabled={disabled} dir="ltr"
                                                className={errors[`order_quantity_${index}`] ? 'error' : ''} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="number" 
                                                value={item.order_rolls || ''}
                                                onChange={(e) => {
                                                    onItemChange(index, 'order_rolls', e.target.value);
                                                    if (!item.manual_total_rolls && e.target.value) {
                                                        onItemChange(index, 'manual_total_rolls', e.target.value);
                                                    }
                                                }}
                                                step="1" min="0" placeholder="Rolls" disabled={disabled} dir="ltr" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="number" value={item.manual_total_yards || ''}
                                                onChange={(e) => {
                                                    onItemChange(index, 'manual_total_yards', e.target.value);
                                                    onItemChange(index, 'quantity', e.target.value);
                                                    if (!item.order_quantity) {
                                                        onItemChange(index, 'order_quantity', e.target.value);
                                                    }
                                                }}
                                                step="1" min="0" placeholder="Stock yards" disabled={disabled} dir="ltr"
                                                className={errors[`manual_total_yards_${index}`] ? 'error' : ''} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-field">
                                            <input type="number" value={item.price || ''}
                                                onChange={(e) => onItemChange(index, 'price', e.target.value)}
                                                step="1" min="0" placeholder="0" disabled={disabled} dir="ltr" />
                                        </div>
                                    </td>
                                    <td><div className="table-field"><span className="total-amount">{currencySymbol} {totalAmount}</span></div></td>
                                    <td>
                                        <button type="button" className="table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Order Items Table Component for View Modal - UPDATED with new fields
const OrderItemsTable = ({ items, currencySymbol = 'Rs', translations }) => {
    if (!items || items.length === 0) {
        return <div className="no-items-message"><p>No items found for this order</p></div>;
    }

    const totalOrderQuantity = items.reduce((sum, item) => sum + (parseFloat(item.order_quantity) || 0), 0);
    const totalOrderRolls = items.reduce((sum, item) => sum + (parseInt(item.order_rolls) || 0), 0);
    const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0);

    return (
        <div className="order-items-table-container">
            <table className="order-items-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{translations.productName || 'Product'}</th>
                        <th>{translations.colorCode || 'Color'}</th>
                        <th>{translations.unit || 'Unit'}</th>
                        <th>{translations.orderQuantity || 'Order Qty'}</th>
                        <th>{translations.orderRolls || 'Order Rolls'}</th>
                        <th>{translations.price || 'Price'}</th>
                        <th>{translations.total || 'Total'}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => {
                        const orderQuantity = parseFloat(item.order_quantity) || parseFloat(item.total_yards) || 0;
                        const orderRolls = parseInt(item.order_rolls) || '';
                        const price = parseFloat(item.price) || 0;
                        const total = parseFloat(item.total_amount) || (price * orderQuantity) || 0;
                        return (
                            <tr key={item.id || index}>
                                <td>{index + 1}</td>
                                <td>{item.product_name || item.name || 'Unknown Product'}</td>
                                <td>{item.product_colorcode || '-'}</td>
                                <td className="centered-cell">{item.unit || 'yards'}</td>
                                <td className="centered-cell"><strong>{orderQuantity.toFixed(2)}</strong></td>
                                <td className="centered-cell">{orderRolls || '-'}</td>
                                <td>{currencySymbol} {price.toFixed(2)}</td>
                                <td className="total-amount">{currencySymbol} {total.toFixed(2)}</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan="4" className="text-right"><strong>{translations.totalAmount || 'Total'}:</strong></td>
                        <td className="centered-cell"><strong>{totalOrderQuantity.toFixed(2)}</strong></td>
                        <td className="centered-cell"><strong>{totalOrderRolls || '-'}</strong></td>
                        <td></td>
                        <td className="total-amount"><strong>{currencySymbol} {totalAmount.toFixed(2)}</strong></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 550, label: 'Narrow', icon: faMinus },
    medium: { width: 1100, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 1300, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

// Main Component
const PlaceorderList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Products state for edit modal
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const [loadingProducts, setLoadingProducts] = useState(false);
    const productSearchRefs = useRef({});

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 1100, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({ show: false, type: '', message: '', direction: 'ltr', progress: 100, timeLeft: 3 });
    const toastTimerRef = useRef(null);

    // Edit form state - UPDATED with all new fields
    const [editFormData, setEditFormData] = useState({
        id: null,
        order_date: '',
        expected_delivery: '',
        order_number: '',
        supplierid: '',
        supplier_name: '',
        company: '',
        status: 'Order Placed',
        container_no: '',
        route_details: '',
        paid_amount: '0.00',
        payment_method: '',
        remarks: '',
        from_bank_name: '',
        from_account_number: '',
        to_bank_name: '',
        to_account_number: '',
        bank_reference: '',
        payment_receipt: null,
        cheque_number: '',
        cheque_date: '',
        cheque_bank_name: '',
        cheque_branch: '',
        cheque_image: null,
        card_last_four: '',
        card_holder_name: '',
        card_expiry: '',
        card_transaction_id: '',
        bill_receipt: null,
        order_receipt: null,
        currency_code: 'PKR',
        currency_symbol: 'Rs',
        currency_rate: 1.0000,
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({ businessname: '', bussinesslogo: '', businessaddress: '', fullname: '', email: '' });

    // Currency state
    const [currentCurrency, setCurrentCurrency] = useState(() => {
        const saved = localStorage.getItem('ourzest_currency');
        return saved ? JSON.parse(saved) : { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', flag: '🇵🇰' };
    });

    // Get theme and language
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('ourzest_theme') || '#10b981');
    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [filterBy, setFilterBy] = useState('all');
    const [orderNumberFilter, setOrderNumberFilter] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'Order Placed', label: 'Order Placed' },
        { value: 'Processing', label: 'Processing' },
        { value: 'Shipped', label: 'Shipped' },
        { value: 'Delivered', label: 'Delivered' },
        { value: 'Cancelled', label: 'Cancelled' }
    ];

    const paymentMethodOptions = [
        { value: '', label: 'All Payment Methods' },
        { value: 'Transfer (Transaction OUT)', label: 'Transfer (Transaction OUT)' },
        { value: 'Cash', label: 'Cash' },
        { value: 'Bank Transfer', label: 'Bank Transfer' },
        { value: 'Cheque', label: 'Cheque' },
        { value: 'Credit Card', label: 'Credit Card' }
    ];

    // Calculate totals
    const totalOrders = filteredOrders.length;
    const totalAmount = filteredOrders.reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0);
    const totalPaid = filteredOrders.reduce((sum, order) => sum + (parseFloat(order.paid_amount) || 0), 0);
    const totalBalance = filteredOrders.reduce((sum, order) => sum + (parseFloat(order.balance) || 0), 0);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentOrders = filteredOrders.slice(startIndex, endIndex);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern, .btn-add-item-table, .table-remove-btn')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX, y: e.clientY,
            width: rect.width, height: rect.height,
            left: rect.left, top: rect.top,
            minWidth: 450, minHeight: 550,
            maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('medium'); setIsModalInitialized(true); }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Show toast function
    const showToast = (type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';
        setToast({ show: true, type, message, direction, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            setToast(prev => ({ ...prev, progress: (timeLeft / 3) * 100, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);
        toastTimerRef.current = timer;
    };

    useEffect(() => { return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); }; }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16), g = parseInt(hex.substring(2, 4), 16), b = parseInt(hex.substring(4, 6), 16);
                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount)), newG = Math.max(0, Math.min(255, g + amount)), newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };
                const palette = {
                    'customer-primary-color': color, 'customer-primary-dark': adjust(-40), 'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20), 'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };
                Object.entries(palette).forEach(([key, value]) => document.documentElement.style.setProperty(`--${key}`, value));
                document.documentElement.style.setProperty('--primary-color', color);
            } catch (err) { console.error("Error applying theme:", err); }
        };
        applyTheme(currentTheme);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (theme !== currentTheme) { setCurrentTheme(theme); applyTheme(theme); }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => { window.removeEventListener('storage', handleThemeChange); clearInterval(interval); };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await placeOrderListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) setCurrentLanguage(lang);
        };
        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);
        return () => { window.removeEventListener('storage', handleLanguageChange); clearInterval(interval); };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Load products for edit modal
    const loadProducts = async () => {
        if (!user?.username || !currentShop?.shop_id) return;
        setLoadingProducts(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', username: user.username, shop_id: currentShop.shop_id, page: 1, limit: 100 })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setProducts(data.data || []);
                setFilteredProducts(data.data || []);
            }
        } catch (error) { console.error('Error loading products:', error); }
        finally { setLoadingProducts(false); }
    };

    // Handle product search
    const handleProductSearch = async (index, term) => {
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));
        if (!user?.username || !currentShop?.shop_id) return;
        if (term.trim() === '') {
            setFilteredProducts(products);
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
            return;
        }
        const filtered = products.filter(product =>
            (product.product_name && product.product_name.toLowerCase().includes(term.toLowerCase())) ||
            (product.product_colorcode && product.product_colorcode.toLowerCase().includes(term.toLowerCase()))
        );
        if (filtered.length > 0) {
            setFilteredProducts(filtered);
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
        } else {
            setLoadingProducts(true);
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_records', username: user.username, shop_id: currentShop.shop_id, filters: { product_name: term }, page: 1, limit: 20 })
                });
                const data = await response.json();
                if (data.status === 'success') {
                    setFilteredProducts(data.data || []);
                    setShowProductDropdown(prev => ({ ...prev, [index]: data.data && data.data.length > 0 }));
                } else { setFilteredProducts([]); }
            } catch (error) { console.error('Error searching products:', error); setFilteredProducts([]); }
            finally { setLoadingProducts(false); }
        }
    };

    // Select product for edit
    const selectProduct = async (index, product) => {
        try {
            const productData = {
                product_id: product.id,
                product_name: product.product_name || '',
                product_colorcode: product.product_colorcode || '',
                category: product.type || '',
                manual_total_yards: '',
                manual_total_rolls: '',
                price: ''
            };
            const updatedItems = [...editFormData.items];
            if (!updatedItems[index]) updatedItems[index] = {};
            updatedItems[index] = {
                ...updatedItems[index],
                product_id: productData.product_id,
                product_name: productData.product_name,
                product_colorcode: productData.product_colorcode,
                price: productData.price,
                is_manual_totals: true
            };
            setEditFormData(prev => ({ ...prev, items: updatedItems }));
            const displayName = productData.product_name + (productData.product_colorcode ? ` - ${productData.product_colorcode}` : '');
            setProductSearchTerms(prev => ({ ...prev, [index]: displayName }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
            showToast('success', `Product "${productData.product_name}" selected`);
        } catch (error) {
            console.error('Error selecting product:', error);
        }
    };

    // Add item to edit form - UPDATED with new fields
    const addEditItem = () => {
        const newItem = {
            id: Date.now() + Math.random(),
            product_id: '', 
            product_name: '', 
            product_colorcode: '',
            unit: 'yards',
            order_quantity: '',
            order_rolls: '',
            manual_total_yards: '', 
            manual_total_rolls: '', 
            price: '', 
            quantity: '',
            is_manual_totals: true
        };
        setEditFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
    };

    // Remove item from edit form
    const removeEditItem = (index) => {
        setEditFormData(prev => {
            if (prev.items.length > 1) {
                const updatedItems = [...prev.items];
                updatedItems.splice(index, 1);
                return { ...prev, items: updatedItems };
            }
            return prev;
        });
    };

    // Handle item change in edit form - UPDATED with new fields
    const handleEditItemChange = (index, field, value) => {
        setEditFormData(prev => {
            const updatedItems = [...prev.items];
            if (!updatedItems[index]) updatedItems[index] = {};
            updatedItems[index] = { ...updatedItems[index], [field]: value };
            
            // Auto-fill fields
            if (field === 'order_quantity') {
                if (!updatedItems[index].manual_total_yards) {
                    updatedItems[index].manual_total_yards = value;
                    updatedItems[index].quantity = value;
                }
            }
            if (field === 'order_rolls') {
                if (!updatedItems[index].manual_total_rolls && value) {
                    updatedItems[index].manual_total_rolls = value;
                }
            }
            if (field === 'manual_total_yards') {
                updatedItems[index].quantity = value;
                if (!updatedItems[index].order_quantity) {
                    updatedItems[index].order_quantity = value;
                }
            }
            if (field === 'manual_total_rolls') {
                if (!updatedItems[index].order_rolls && value) {
                    updatedItems[index].order_rolls = value;
                }
            }
            
            return { ...prev, items: updatedItems };
        });
        const errorKey = `${field}_${index}`;
        if (editFormErrors[errorKey]) setEditFormErrors(prev => { const newErrors = { ...prev }; delete newErrors[errorKey]; return newErrors; });
    };

    // Calculate grand total for edit form - uses order_quantity
    const calculateEditGrandTotal = () => {
        return editFormData.items.reduce((sum, item) => {
            const qty = parseFloat(item.order_quantity) || parseFloat(item.manual_total_yards) || 0;
            const price = parseFloat(item.price) || 0;
            return sum + (price * qty);
        }, 0).toFixed(2);
    };

    // Fetch orders
    const fetchPlaceOrders = async () => {
        if (!user || !user.username) { showToast('error', 'User not authenticated'); setLoading(false); return; }
        if (!currentShop) { setLoading(false); return; }
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', username: user.username, shop_id: currentShop.shop_id, filters: {} })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setOrders(data.data || []);
                setFilteredOrders(data.data || []);
            } else { showToast('error', data.message || 'Network error occurred'); }
        } catch (error) { console.error('Error fetching orders:', error); showToast('error', 'Network error occurred'); }
        finally { setLoading(false); }
    };

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_business_info', username: user.username })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({ businessname: data.businessname || '', bussinesslogo: data.bussinesslogo || '', businessaddress: data.businessaddress || '', fullname: data.fullname || '', email: data.email || '' });
            } else {
                setBusinessInfo({ businessname: user.business_name || '', bussinesslogo: '', businessaddress: '', fullname: user.fullname || '', email: user.email || '' });
            }
        } catch (error) { console.error('Error fetching business info:', error); }
    };

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) { console.error('Error loading shops:', error); setAvailableShops([]); }
        finally { setLoadingShops(false); }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) { showToast('success', 'Shop switched successfully'); fetchPlaceOrders(); }
            else { showToast('error', 'Failed to switch shop'); }
        } catch (error) { console.error('Error switching shop:', error); showToast('error', 'Failed to switch shop'); }
    };

    // Apply filters
    useEffect(() => {
        let filtered = [...orders];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order => order.order_number?.toLowerCase().includes(term) || order.supplier_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'orderNumber' && orderNumberFilter.trim()) {
            filtered = filtered.filter(order => order.order_number?.toLowerCase().includes(orderNumberFilter.toLowerCase()));
        }
        if (filterBy === 'supplier' && supplierFilter.trim()) {
            filtered = filtered.filter(order => order.supplier_name?.toLowerCase().includes(supplierFilter.toLowerCase()));
        }
        if (filterBy === 'status' && statusFilter) {
            filtered = filtered.filter(order => order.status === statusFilter);
        }
        if (filterBy === 'paymentMethod' && paymentMethodFilter) {
            filtered = filtered.filter(order => order.payment_method === paymentMethodFilter);
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            filtered = filtered.filter(order => (order.added_by_username?.toLowerCase().includes(addedByFilter.toLowerCase())) || (order.username?.toLowerCase().includes(addedByFilter.toLowerCase())));
        }
        if (startDate) {
            const start = new Date(startDate); start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(order => { if (!order.order_date) return false; const orderDate = new Date(order.order_date); orderDate.setHours(0, 0, 0, 0); return orderDate >= start; });
        }
        if (endDate) {
            const end = new Date(endDate); end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(order => { if (!order.order_date) return false; const orderDate = new Date(order.order_date); return orderDate <= end; });
        }
        setFilteredOrders(filtered);
        setCurrentPage(1);
    }, [orders, searchTerm, filterBy, orderNumberFilter, supplierFilter, statusFilter, paymentMethodFilter, addedByFilter, startDate, endDate]);

    const resetFilters = () => { setFilterBy('all'); setOrderNumberFilter(''); setSupplierFilter(''); setStatusFilter(''); setPaymentMethodFilter(''); setAddedByFilter(''); setStartDate(''); setEndDate(''); setSearchTerm(''); };

    // View order
    const handleView = async (order) => {
        setLoadingOrderDetails(true);
        try {
            if (order.items && order.items.length > 0) {
                setSelectedOrder(order);
                setShowViewModal(true);
            } else {
                const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_record', id: order.id, username: user?.username, shop_id: currentShop?.shop_id })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) { setSelectedOrder(data.data); setShowViewModal(true); }
                else { showToast('error', 'Failed to load order details'); setSelectedOrder(order); setShowViewModal(true); }
            }
        } catch (error) { console.error('Error fetching order details:', error); showToast('error', 'Failed to load order details'); setSelectedOrder(order); setShowViewModal(true); }
        finally { setLoadingOrderDetails(false); }
    };

    // Edit order - UPDATED with all new fields
    const handleEdit = (order) => {
        if (order.username !== user.username) { showToast('error', 'You can only edit your own purchase orders'); return; }
        // Load products for edit modal
        loadProducts();
        setEditFormData({
            id: order.id,
            order_date: order.order_date || '',
            expected_delivery: order.expected_delivery || '',
            order_number: order.order_number || '',
            supplierid: order.supplierid || '',
            supplier_name: order.supplier_name || '',
            company: order.company || '',
            status: order.status || 'Order Placed',
            container_no: order.container_no || '',
            route_details: order.route_details || '',
            paid_amount: order.paid_amount || '0.00',
            payment_method: order.payment_method || '',
            remarks: order.remarks || '',
            from_bank_name: order.from_bank_name || '',
            from_account_number: order.from_account_number || '',
            to_bank_name: order.to_bank_name || '',
            to_account_number: order.to_account_number || '',
            bank_reference: order.bank_reference || '',
            payment_receipt: order.payment_receipt || null,
            cheque_number: order.cheque_number || '',
            cheque_date: order.cheque_date || '',
            cheque_bank_name: order.cheque_bank_name || '',
            cheque_branch: order.cheque_branch || '',
            cheque_image: order.cheque_image || null,
            card_last_four: order.card_last_four || '',
            card_holder_name: order.card_holder_name || '',
            card_expiry: order.card_expiry || '',
            card_transaction_id: order.card_transaction_id || '',
            bill_receipt: order.bill_receipt || null,
            order_receipt: order.order_receipt || null,
            currency_code: order.currency_code || 'PKR',
            currency_symbol: order.currency_symbol || 'Rs',
            currency_rate: parseFloat(order.currency_rate) || 1.0000,
            items: (order.items || []).map(item => ({
                id: item.id || Date.now() + Math.random(),
                product_id: item.product_id || '',
                product_name: item.product_name || '',
                product_colorcode: item.product_colorcode || '',
                unit: item.unit || 'yards',
                order_quantity: item.order_quantity || item.manual_total_yards || item.total_yards || '',
                order_rolls: item.order_rolls || '',
                manual_total_yards: item.manual_total_yards || item.total_yards || '',
                manual_total_rolls: item.manual_total_rolls || '',
                price: item.price || '',
                quantity: item.manual_total_yards || item.total_yards || '',
                is_manual_totals: true
            }))
        });
        if (order.items && order.items.length > 0) {
            const searchTerms = {};
            order.items.forEach((item, idx) => {
                searchTerms[idx] = (item.product_name || '') + (item.product_colorcode ? ` - ${item.product_colorcode}` : '');
            });
            setProductSearchTerms(searchTerms);
        }
        setEditFormErrors({});
        setShowEditModal(true);
    };

    // Submit edit order - UPDATED payload
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        // Validate form
        const newErrors = {};
        if (!editFormData.supplierid) newErrors.supplier = 'Supplier is required';
        editFormData.items.forEach((item, index) => {
            if (!item.order_quantity && !item.manual_total_yards) {
                newErrors[`order_quantity_${index}`] = 'Order quantity is required';
            }
        });
        if (!editFormData.status) newErrors.status = 'Status is required';
        if (Object.keys(newErrors).length > 0) { setEditFormErrors(newErrors); showToast('error', 'Please fix validation errors'); return; }

        setEditLoading(true);
        try {
            const currentUsername = user?.username;
            const currentShopId = currentShop?.shop_id;
            if (!currentUsername || !currentShopId) throw new Error("User or shop not authenticated");

            const itemsData = editFormData.items.map(item => {
                const orderQuantity = parseFloat(item.order_quantity) || parseFloat(item.manual_total_yards) || 0;
                const orderRolls = item.order_rolls ? parseInt(item.order_rolls) : (item.manual_total_rolls ? parseInt(item.manual_total_rolls) : null);
                const stockYards = item.manual_total_yards ? parseFloat(item.manual_total_yards) : null;
                const stockRolls = item.manual_total_rolls ? parseInt(item.manual_total_rolls) : null;
                const price = parseFloat(item.price) || 0;
                return {
                    product_id: item.product_id ? parseInt(item.product_id) : null,
                    product_name: item.product_name || '',
                    product_colorcode: item.product_colorcode || null,
                    category: item.category || null,
                    unit: item.unit || 'yards',
                    order_quantity: orderQuantity,
                    order_rolls: orderRolls,
                    manual_total_yards: stockYards || orderQuantity,
                    manual_total_rolls: stockRolls,
                    is_manual_totals: true,
                    price: price,
                    quantity: orderQuantity,
                    total_amount: (price * orderQuantity).toFixed(0)
                };
            });

            const payload = {
                action: 'edit',
                id: editFormData.id,
                username: currentUsername,
                shop_id: parseInt(currentShopId),
                order_date: editFormData.order_date,
                expected_delivery: editFormData.expected_delivery || null,
                order_number: editFormData.order_number,
                supplierid: parseInt(editFormData.supplierid) || 0,
                supplier_name: editFormData.supplier_name || '',
                company: editFormData.company || null,
                status: editFormData.status,
                container_no: editFormData.container_no || null,
                route_details: editFormData.route_details || null,
                paid_amount: parseFloat(editFormData.paid_amount || 0),
                payment_method: editFormData.payment_method,
                remarks: editFormData.remarks || null,
                items: itemsData,
                from_bank_name: editFormData.from_bank_name || null,
                from_account_number: editFormData.from_account_number || null,
                to_bank_name: editFormData.to_bank_name || null,
                to_account_number: editFormData.to_account_number || null,
                bank_reference: editFormData.bank_reference || null,
                payment_receipt: editFormData.payment_receipt,
                cheque_number: editFormData.cheque_number || null,
                cheque_date: editFormData.cheque_date || null,
                cheque_bank_name: editFormData.cheque_bank_name || null,
                cheque_branch: editFormData.cheque_branch || null,
                cheque_image: editFormData.cheque_image,
                card_last_four: editFormData.card_last_four || null,
                card_holder_name: editFormData.card_holder_name || null,
                card_expiry: editFormData.card_expiry || null,
                card_transaction_id: editFormData.card_transaction_id || null,
                bill_receipt: editFormData.bill_receipt,
                currency_code: editFormData.currency_code,
                currency_symbol: editFormData.currency_symbol,
                currency_rate: editFormData.currency_rate,
                order_receipt: editFormData.order_receipt
            };

            // Payload size check
            const payloadStr = JSON.stringify(payload);
            const payloadSizeMB = (new Blob([payloadStr]).size / 1024 / 1024).toFixed(2);
            if (parseFloat(payloadSizeMB) > 7) {
                showToast('error', `Payload too large (${payloadSizeMB}MB). Please reduce file sizes.`);
                setEditLoading(false);
                return;
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const responseText = await response.text();
            let data;
            try { data = JSON.parse(responseText); }
            catch (parseError) { throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 200)}`); }

            if (data.status === 'success') {
                showToast('success', translations.orderUpdated || 'Order updated successfully!');
                setShowEditModal(false);
                fetchPlaceOrders();
            } else { showToast('error', data.message || translations.updateError || 'Error updating order'); }
        } catch (error) { console.error('Update error:', error); showToast('error', error.message || translations.updateError || 'Error updating order'); }
        finally { setEditLoading(false); }
    };

    const handleDeleteClick = (orderId) => {
        const order = orders.find(o => o.id === orderId);
        if (!order || order.username !== user.username) { showToast('error', 'You can only delete your own purchase orders'); return; }
        setOrderToDelete(orderId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!orderToDelete || !currentShop) return;
        const order = orders.find(o => o.id === orderToDelete);
        if (!order || order.username !== user.username) { showToast('error', 'You can only delete your own purchase orders'); setShowDeleteModal(false); setOrderToDelete(null); return; }
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fabrics_order.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete', id: orderToDelete, username: user.username, shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success') { showToast('success', translations.deletedSuccess || 'Purchase order deleted successfully'); fetchPlaceOrders(); setShowDeleteModal(false); setOrderToDelete(null); }
            else { showToast('error', data.message || translations.deleteError || 'Error deleting purchase order'); }
        } catch (error) { console.error('Error deleting order:', error); showToast('error', translations.deleteError || 'Error deleting purchase order'); }
    };

    const handlePageChange = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };
    const formatDate = (dateString) => { if (!dateString) return '-'; try { const date = new Date(dateString); if (isNaN(date.getTime())) return dateString; const year = date.getFullYear(); const month = String(date.getMonth() + 1).padStart(2, '0'); const day = String(date.getDate()).padStart(2, '0'); return `${year}-${month}-${day}`; } catch { return dateString; } };
    const formatNumber = (num, decimals = 2) => { if (num === undefined || num === null || num === '') return ''; const value = parseFloat(num).toFixed(decimals); return value.replace(/\.?0+$/, ''); };
    const getStatusBadge = (status) => {
        switch (status) {
            case 'Order Placed': return { bg: '#10b98120', color: '#10b981', border: '#10b98140' };
            case 'Processing': return { bg: '#f59e0b20', color: '#f59e0b', border: '#f59e0b40' };
            case 'Shipped': return { bg: '#3b82f620', color: '#3b82f6', border: '#3b82f640' };
            case 'Delivered': return { bg: '#10b98120', color: '#10b981', border: '#10b98140' };
            case 'Cancelled': return { bg: '#ef444420', color: '#ef4444', border: '#ef444440' };
            default: return { bg: '#6b728020', color: '#6b7280', border: '#6b728040' };
        }
    };
    const getTodayDate = () => new Date().toISOString().split('T')[0];

    useEffect(() => { if (startDate && endDateRef.current) endDateRef.current.min = startDate; }, [startDate]);
    useEffect(() => { const today = getTodayDate(); if (startDateRef.current) startDateRef.current.max = today; if (endDateRef.current) endDateRef.current.max = today; }, []);

    useEffect(() => { if (user && user.username) { fetchPlaceOrders(); fetchBusinessInfo(); fetchAvailableShops(); } else { setLoading(false); } }, [user, currentShop]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            <ShopSelectorModal isOpen={showShopSelector} onClose={() => setShowShopSelector(false)} shops={availableShops} currentShop={currentShop} onSelectShop={handleSwitchShop} translations={translations} loading={loadingShops} />

            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
                    <div className="header-text">
                        <h1>{translations.placeOrderList || 'Purchase Orders List'}</h1>
                        <div className="shop-info"><p>{currentShop?.shop_name ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}` : `${translations.allPlaceOrders || 'My Purchase Orders'} - ${user?.username}`}</p></div>
                    </div>
                </div>
                {!currentShop && (
                    <div className="shop-indicator-modern no-shop">
                        <div className="shop-badge"><FontAwesomeIcon icon={faExclamationTriangle} /><span className="shop-warning">{translations.noShopSelected || 'No shop selected'}</span></div>
                        <button className="btn-modern btn-switch-shop" onClick={() => setShowShopSelector(true)}><FontAwesomeIcon icon={faStore} /><span>{translations.switchShop || 'Switch Shop'}</span></button>
                    </div>
                )}
            </div>

            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact"><select value={filterBy} onChange={(e) => setFilterBy(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                        <option value="all">{translations.all || 'All'}</option>
                        <option value="orderNumber">{translations.orderNumberFilter || 'Order Number'}</option>
                        <option value="supplier">{translations.supplierFilter || 'Supplier'}</option>
                        <option value="status">{translations.statusFilter || 'Status'}</option>
                        <option value="paymentMethod">{translations.paymentMethod || 'Payment Method'}</option>
                        <option value="date">{translations.dateRange || 'Date Range'}</option>
                        <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                    </select></div>
                    {filterBy === 'orderNumber' && (<div className="filter-group compact"><input type="text" value={orderNumberFilter} onChange={(e) => setOrderNumberFilter(e.target.value)} placeholder={translations.orderNumberFilter || 'Order number...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} /></div>)}
                    {filterBy === 'supplier' && (<div className="filter-group compact"><input type="text" value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} placeholder={translations.supplierFilter || 'Supplier name...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} /></div>)}
                    {filterBy === 'status' && (<div className="filter-group compact"><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>{statusOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}</select></div>)}
                    {filterBy === 'paymentMethod' && (<div className="filter-group compact"><select value={paymentMethodFilter} onChange={(e) => setPaymentMethodFilter(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>{paymentMethodOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}</select></div>)}
                    {filterBy === 'added_by' && (<div className="filter-group compact"><input type="text" value={addedByFilter} onChange={(e) => setAddedByFilter(e.target.value)} placeholder={translations.addedByFilter || 'Added by username...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} /></div>)}
                    {filterBy === 'date' && (<><div className="filter-group compact date-filter"><input ref={startDateRef} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={getTodayDate()} className="filter-input compact date-input" title={translations.startDate || 'Start Date'} /></div><div className="filter-group compact date-filter"><input ref={endDateRef} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} max={getTodayDate()} className="filter-input compact date-input" title={translations.endDate || 'End Date'} /></div></>)}
                    <div className="filter-group compact search-group"><div className="search-box compact"><input type="text" placeholder={translations.searchPlaceholder || 'Search purchase orders...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && setFilteredOrders([...orders].filter(o => o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) || o.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())))} dir={isRTL ? 'rtl' : 'ltr'} /></div></div>
                    <div className="filter-group compact"><button onClick={fetchPlaceOrders} className="refresh-btn-modern compact" disabled={loading} title={translations.refresh || 'Refresh'}><FontAwesomeIcon icon={faRefresh} /><span className="btn-text">{translations.refresh || 'Refresh'}</span></button></div>
                    {(filterBy !== 'all' || searchTerm) && (<div className="filter-group compact"><button onClick={resetFilters} className="reset-filters-btn compact" title={translations.resetFilters || 'Reset Filters'}><FontAwesomeIcon icon={faTimes} /><span className="btn-text">{translations.resetFilters || 'Reset'}</span></button></div>)}
                </div>
            </div>

            {loading ? (
                <div className="loading-container"><div className="loading-spinner-modern"></div><p>{translations.loadingUserData || 'Loading your purchase order data...'}</p></div>
            ) : !currentShop ? (
                <div className="no-shop-container"><div className="no-shop-message"><FontAwesomeIcon icon={faStore} /><h3>{translations.noShopSelected || 'No Shop Selected'}</h3><p>Please select a shop to view purchase orders</p><button className="btn-modern btn-primary-modern" onClick={() => setShowShopSelector(true)}><FontAwesomeIcon icon={faExchangeAlt} /><span>{translations.switchShop || 'Switch Shop'}</span></button></div></div>
            ) : (
                <>
                    <div className="summary-cards">
                        <div className="summary-card"><div className="summary-icon"><FontAwesomeIcon icon={faShoppingCart} /></div><div className="summary-info"><span className="summary-label">{translations.totalOrders || 'Total Orders'}</span><span className="summary-value">{totalOrders}</span></div></div>
                        <div className="summary-card"><div className="summary-icon"><FontAwesomeIcon icon={faMoneyBillWave} /></div><div className="summary-info"><span className="summary-label">{translations.totalValue || 'Total Value'}</span><span className="summary-value">{currentCurrency.symbol} {formatNumber(totalAmount)}</span></div></div>
                        <div className="summary-card"><div className="summary-icon"><FontAwesomeIcon icon={faCheckCircle} /></div><div className="summary-info"><span className="summary-label">{translations.paidAmount || 'Total Paid'}</span><span className="summary-value paid">{currentCurrency.symbol} {formatNumber(totalPaid)}</span></div></div>
                        <div className="summary-card"><div className="summary-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div><div className="summary-info"><span className="summary-label">{translations.pendingAmount || 'Pending Amount'}</span><span className="summary-value pending">{currentCurrency.symbol} {formatNumber(totalBalance)}</span></div></div>
                    </div>

                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.orderNumber || 'Order #'}</th>
                                            <th className="centered-cell">{translations.orderDate || 'Date'}</th>
                                            <th className="centered-cell">{translations.supplierName || 'Supplier'}</th>
                                            <th className="centered-cell">{translations.totalAmount || 'Total'}</th>
                                            <th className="centered-cell">{translations.paidAmount || 'Paid'}</th>
                                            <th className="centered-cell">{translations.balance || 'Balance'}</th>
                                            <th className="centered-cell">{translations.status || 'Status'}</th>
                                            <th className="centered-cell">{translations.paymentMethod || 'Payment'}</th>
                                            <th className="centered-cell">{translations.itemsCount || 'Items'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrders.map((order, index) => {
                                            const addedBy = order.added_by_username || order.username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            const statusStyle = getStatusBadge(order.status);
                                            const itemsCount = order.items ? order.items.length : 0;
                                            return (
                                                <tr key={order.id}>
                                                    <td className="id-cell centered-cell"><span className="cell-text">{startIndex + index + 1}</span></td>
                                                    <td className="centered-cell"><span className="cell-text order-number">{order.order_number || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{formatDate(order.order_date)}</span></td>
                                                    <td className="centered-cell"><span className="cell-text">{order.supplier_name}</span></td>
                                                    <td className="centered-cell"><span className="cell-text total-amount">{order.currency_symbol || 'Rs'} {formatNumber(order.total_amount)}</span></td>
                                                    <td className="centered-cell"><span className="cell-text paid-amount">{order.currency_symbol || 'Rs'} {formatNumber(order.paid_amount)}</span></td>
                                                    <td className="centered-cell"><span className={`cell-text balance ${parseFloat(order.balance) > 0 ? 'negative' : 'positive'}`}>{order.currency_symbol || 'Rs'} {formatNumber(order.balance)}</span></td>
                                                    <td className="centered-cell"><div className="status-badge" style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>{order.status || '-'}</div></td>
                                                    <td className="centered-cell"><span className="cell-text">{order.payment_method || '-'}</span></td>
                                                    <td className="centered-cell"><span className="cell-text items-count">{itemsCount}</span></td>
                                                    <td className="centered-cell"><span className={`added-by-badge ${isSelfAdded ? 'self' : ''}`}><span className="cell-text">{addedBy}</span></span></td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="actions-content">
                                                            <button onClick={() => handleView(order)} className="action-btn-modern view-btn-modern" title={translations.view || 'View'}><FontAwesomeIcon icon={faEye} /></button>
                                                            <button onClick={() => handleEdit(order)} className="action-btn-modern edit-btn-modern" title={translations.edit || 'Edit'} disabled={order.username !== user?.username}><FontAwesomeIcon icon={faEdit} /></button>
                                                            <button onClick={() => handleDeleteClick(order.id)} className="action-btn-modern delete-btn-modern" title={translations.delete || 'Delete'} disabled={order.username !== user?.username}><FontAwesomeIcon icon={faTrash} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    {filteredOrders.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info"><span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredOrders.length)} {translations.of || 'of'} {filteredOrders.length} {translations.orders || 'orders'}</span>{currentShop?.shop_name && (<span className="shop-info-pagination"> | {translations.shopName || 'Shop'}: {currentShop.shop_name}</span>)}</div>
                            <div className="pagination-controls"><button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn"><FontAwesomeIcon icon={faChevronLeft} /><span>{translations.previous || 'Previous'}</span></button><div className="page-numbers">{Array.from({ length: Math.min(5, totalPages) }, (_, i) => { let pageNum; if (totalPages <= 5) pageNum = i + 1; else if (currentPage <= 3) pageNum = i + 1; else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i; else pageNum = currentPage - 2 + i; return (<button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`page-number ${currentPage === pageNum ? 'active' : ''}`}>{pageNum}</button>); })}</div><button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn"><span>{translations.next || 'Next'}</span><FontAwesomeIcon icon={faChevronRight} /></button></div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedOrder && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
                            <div className="modal-title"><h3>{translations.orderDetails || 'Purchase Order Details'}</h3><span className="order-number-badge">#{selectedOrder.order_number}</span></div>
                            <button className="close-btn-modern" onClick={() => setShowViewModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            {loadingOrderDetails ? (<div className="loading-container"><div className="loading-spinner-modern"></div><p>Loading order details...</p></div>) : (
                                <>
                                    <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faFileInvoice} />Order Summary</h4><div className="view-grid">
                                        <div className="view-row"><span className="view-label">{translations.orderId || 'Order ID'}:</span><span className="view-value">#{selectedOrder.id}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.orderNumber || 'Order Number'}:</span><span className="view-value">{selectedOrder.order_number || '-'}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.orderDate || 'Order Date'}:</span><span className="view-value">{formatDate(selectedOrder.order_date)}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.expectedDelivery || 'Expected Delivery'}:</span><span className="view-value">{formatDate(selectedOrder.expected_delivery) || '-'}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.status || 'Status'}:</span><span className="view-value"><div className="status-badge" style={getStatusBadge(selectedOrder.status)}>{selectedOrder.status}</div></span></div>
                                    </div></div>
                                    <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faUser} />{translations.supplierInfo || 'Supplier Information'}</h4><div className="view-grid">
                                        <div className="view-row"><span className="view-label">{translations.supplierName || 'Supplier Name'}:</span><span className="view-value">{selectedOrder.supplier_name}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.company || 'Company'}:</span><span className="view-value">{selectedOrder.company || '-'}</span></div>
                                    </div></div>
                                    {(selectedOrder.container_no || selectedOrder.route_details) && (<div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faTruck} />{translations.shippingInfo || 'Shipping Information'}</h4><div className="view-grid">
                                        {selectedOrder.container_no && (<div className="view-row"><span className="view-label">{translations.containerNo || 'Container No'}:</span><span className="view-value">{selectedOrder.container_no}</span></div>)}
                                        {selectedOrder.route_details && (<div className="view-row"><span className="view-label">{translations.routeDetails || 'Route Details'}:</span><span className="view-value">{selectedOrder.route_details}</span></div>)}
                                    </div></div>)}
                                    <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faMoneyBillWave} />{translations.paymentInfo || 'Payment Information'}</h4><div className="view-grid">
                                        <div className="view-row"><span className="view-label">{translations.paymentMethod || 'Payment Method'}:</span><span className="view-value">{selectedOrder.payment_method || '-'}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.currency || 'Currency'}:</span><span className="view-value">{selectedOrder.currency_code || 'PKR'}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.totalAmount || 'Total Amount'}:</span><span className="view-value total-amount">{selectedOrder.currency_symbol || 'Rs'} {formatNumber(selectedOrder.total_amount)}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.paidAmount || 'Paid Amount'}:</span><span className="view-value paid-amount">{selectedOrder.currency_symbol || 'Rs'} {formatNumber(selectedOrder.paid_amount)}</span></div>
                                        <div className="view-row"><span className="view-label">{translations.balance || 'Balance'}:</span><span className={`view-value balance ${parseFloat(selectedOrder.balance) > 0 ? 'negative' : 'positive'}`}>{selectedOrder.currency_symbol || 'Rs'} {formatNumber(selectedOrder.balance)}</span></div>
                                    </div></div>
                                    <div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faBoxes} />{translations.productDetails || 'Order Items'}</h4><OrderItemsTable items={selectedOrder.items} currencySymbol={selectedOrder.currency_symbol || 'Rs'} translations={translations} /></div>
                                    {selectedOrder.remarks && (<div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faComment} />{translations.remarks || 'Remarks'}</h4><div className="remarks-box">{selectedOrder.remarks}</div></div>)}
                                    <div className="view-section metadata-section"><div className="view-grid">
                                        <div className="view-row"><span className="view-label">{translations.addedBy || 'Added By'}:</span><span className="view-value"><span className="added-by-badge">{selectedOrder.added_by_username || selectedOrder.username || '-'}</span></span></div>
                                        <div className="view-row"><span className="view-label">{translations.dateAdded || 'Date Added'}:</span><span className="view-value">{formatDate(selectedOrder.created_at) || formatDate(selectedOrder.order_date)}</span></div>
                                        {selectedOrder.shop_name && (<div className="view-row"><span className="view-label">{translations.shopInfo || 'Shop'}:</span><span className="view-value">{selectedOrder.shop_name} {selectedOrder.shop_code ? `(${selectedOrder.shop_code})` : ''}</span></div>)}
                                    </div></div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            {selectedOrder.username === user?.username && (<button className="btn-modern btn-primary-modern" onClick={() => { setShowViewModal(false); handleEdit(selectedOrder); }}><FontAwesomeIcon icon={faEdit} />{translations.edit || 'Edit'}</button>)}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal - MOVABLE, RESIZABLE with updated fields */}
            {showEditModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div ref={modalRef} className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', left: modalPosition.x || '50%', top: modalPosition.y || '50%', transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none', width: modalSize.width, minWidth: '450px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        <div className="modal-header-modern" onMouseDown={startDrag} style={{ cursor: 'move', flexShrink: 0 }}>
                            <div className="modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="modal-title"><h3>{translations.editOrder || 'Edit Purchase Order'}</h3><span className="order-number-badge">#{editFormData.order_number}</span></div>
                            <div className="width-control-group">{Object.entries(widthModes).map(([key, mode]) => (<button key={key} className={`width-btn ${currentWidthMode === key ? 'active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label} type="button"><FontAwesomeIcon icon={mode.icon} /></button>))}</div>
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)} type="button"><FontAwesomeIcon icon={faTimes} /></button>
                        </div>

                        <form onSubmit={handleEditSubmit} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-body-modern" style={{ flex: 1, overflow: 'auto' }}>
                                <div className="edit-form-container">
                                    <div className="form-section">
                                        <h4 className="section-title"><FontAwesomeIcon icon={faFileInvoice} />Order Information</h4>
                                        <div className="form-grid edit-form-grid">
                                            <div className="form-group"><label htmlFor="order_number">{translations.orderNumber || 'Order Number'}</label><input type="text" id="order_number" value={editFormData.order_number} readOnly disabled className="readonly-input" dir="ltr" /></div>
                                            <div className="form-group"><label htmlFor="order_date">{translations.orderDate || 'Order Date'} <span className="required">*</span></label><input type="date" id="order_date" value={editFormData.order_date} onChange={(e) => setEditFormData(prev => ({ ...prev, order_date: e.target.value }))} max={getTodayDate()} dir="ltr" /></div>
                                            <div className="form-group"><label htmlFor="expected_delivery">{translations.expectedDelivery || 'Expected Delivery'}</label><input type="date" id="expected_delivery" value={editFormData.expected_delivery} onChange={(e) => setEditFormData(prev => ({ ...prev, expected_delivery: e.target.value }))} min={editFormData.order_date} dir="ltr" /></div>
                                            <div className="form-group"><label htmlFor="supplier_name">{translations.supplierName || 'Supplier Name'}</label><input type="text" id="supplier_name" value={editFormData.supplier_name} readOnly disabled className="readonly-input" dir={isRTL ? 'rtl' : 'ltr'} /></div>
                                            <div className="form-group"><label htmlFor="status">{translations.status || 'Status'} <span className="required">*</span></label><select id="status" value={editFormData.status} onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))} dir={isRTL ? 'rtl' : 'ltr'}>{statusOptions.filter(opt => opt.value).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}</select></div>
                                            <div className="form-group"><label htmlFor="payment_method">{translations.paymentMethod || 'Payment Method'}</label><select id="payment_method" value={editFormData.payment_method} onChange={(e) => setEditFormData(prev => ({ ...prev, payment_method: e.target.value }))} dir={isRTL ? 'rtl' : 'ltr'}>
                                                <option value="">Select Payment Method</option>
                                                <option value="Transfer (Transaction OUT)">Transfer (Transaction OUT)</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Bank Transfer">Bank Transfer</option>
                                                <option value="Cheque">Cheque</option>
                                                <option value="Credit Card">Credit Card</option>
                                            </select></div>
                                            <div className="form-group"><label htmlFor="paid_amount">{translations.paidAmount || 'Paid Amount'}</label><input type="number" id="paid_amount" value={editFormData.paid_amount} onChange={(e) => setEditFormData(prev => ({ ...prev, paid_amount: e.target.value }))} step="0.01" min="0" dir="ltr" /></div>
                                            <div className="form-group"><label htmlFor="container_no">{translations.containerNo || 'Container No'}</label><input type="text" id="container_no" value={editFormData.container_no} onChange={(e) => setEditFormData(prev => ({ ...prev, container_no: e.target.value }))} dir={isRTL ? 'rtl' : 'ltr'} /></div>
                                            <div className="form-group full-width"><label htmlFor="remarks">{translations.remarks || 'Remarks'}</label><textarea id="remarks" value={editFormData.remarks} onChange={(e) => setEditFormData(prev => ({ ...prev, remarks: e.target.value }))} rows="2" dir={isRTL ? 'rtl' : 'ltr'} /></div>
                                        </div>
                                    </div>

                                    <div className="form-section">
                                        <h4 className="section-title"><FontAwesomeIcon icon={faBoxes} />Order Items <span className="required">*</span></h4>
                                        <EditOrderItemsTable
                                            items={editFormData.items}
                                            onItemChange={handleEditItemChange}
                                            onRemove={removeEditItem}
                                            onAdd={addEditItem}
                                            errors={editFormErrors}
                                            disabled={editLoading}
                                            translations={translations}
                                            isRTL={isRTL}
                                            currencySymbol={editFormData.currency_symbol || 'Rs'}
                                            products={products}
                                            filteredProducts={filteredProducts}
                                            setFilteredProducts={setFilteredProducts}
                                            productSearchTerms={productSearchTerms}
                                            setProductSearchTerms={setProductSearchTerms}
                                            showProductDropdown={showProductDropdown}
                                            setShowProductDropdown={setShowProductDropdown}
                                            loadingProducts={loadingProducts}
                                            productSearchRefs={productSearchRefs}
                                            handleProductSearch={handleProductSearch}
                                            selectProduct={selectProduct}
                                        />
                                        {editFormErrors.items && <div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.items}</div>}
                                    </div>

                                    <div className="form-section payment-summary-section">
                                        <h4 className="section-title"><FontAwesomeIcon icon={faMoneyBillWave} />Payment Summary</h4>
                                        <div className="summary-row">
                                            <div className="summary-item"><span className="summary-label">Grand Total:</span><span className="summary-value">{editFormData.currency_symbol || 'Rs'} {calculateEditGrandTotal()}</span></div>
                                            <div className="summary-item"><span className="summary-label">Paid Amount:</span><span className="summary-value paid">{editFormData.currency_symbol || 'Rs'} {formatNumber(editFormData.paid_amount)}</span></div>
                                            <div className="summary-item"><span className="summary-label">Balance:</span><span className={`summary-value balance ${parseFloat(editFormData.paid_amount) < parseFloat(calculateEditGrandTotal()) ? 'negative' : 'positive'}`}>{editFormData.currency_symbol || 'Rs'} {(parseFloat(calculateEditGrandTotal()) - parseFloat(editFormData.paid_amount || 0)).toFixed(2)}</span></div>
                                        </div>
                                    </div>

                                    <div className="edit-product-info"><p><strong>{translations.orderId || 'Order ID'}:</strong> #{editFormData.id}</p><p><strong>{translations.addedBy || 'Added By'}:</strong> {selectedOrder?.added_by_username || selectedOrder?.username}</p><p><strong>{translations.shopName || 'Shop'}:</strong> {currentShop?.shop_name || 'N/A'}</p></div>
                                </div>
                            </div>
                            <div className="modal-footer-modern" style={{ flexShrink: 0 }}>
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)} disabled={editLoading}><FontAwesomeIcon icon={faTimes} />{translations.cancel || 'Cancel'}</button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>{editLoading ? (<><div className="btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></>) : (<><FontAwesomeIcon icon={faSave} /><span>{translations.saveChanges || 'Save Changes'}</span></>)}</button>
                            </div>
                        </form>
                        {editLoading && (<div className="loading-overlay edit-loading-overlay"><div className="loading-content"><div className="loading-spinner"></div><p>{translations.updating || 'Updating...'}</p><p className="loading-subtext">Please wait while we update order data</p></div></div>)}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header"><div className="modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div><div className="modal-title"><h3 style={{ color: 'var(--customer-error-color)' }}>{translations.confirmDelete || 'Confirm Delete'}</h3></div><button className="close-btn-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button></div>
                        <div className="modal-body-modern"><div className="delete-message"><p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>{translations.deleteMessage || 'Are you sure you want to delete this purchase order?'}</p><p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p></div></div>
                        <div className="modal-footer-modern"><button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button><button type="button" className="btn-modern btn-delete-modern" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlaceorderList;